import { api } from '@/api';
import {
  BeamShapingGroupDto,
  BeamShapingGroupRectangularDto,
  instanceOfBeamShapingGroupRectangularDto,
  instanceOfMvTherapySystemSetupDto,
  instanceOfNumericListDto,
  instanceOfTextValueListDto,
  instanceOfTaskItemDto,
  NumericEvaluationParameterDto,
  QuantityValueType,
  TaskItemAreaDto,
  TaskItemDto,
  TaskTypeDescription,
} from '@/api/generator';
import { useActionWithConfirmation } from '@/components/confirmationModals/useActionWithConfirmation';
import { CustomValidateFunction } from '@/components/forms/types.ts';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import { TranslatableEntity, translateEntityProperty, truthy } from '@/utils/helper';
import { getFormattingOptionsFromEvaluationParameter, toDisplayValue } from '@/utils/numberUtils';
import { t } from 'i18next';
import { FieldArrayContext } from 'vee-validate';
import { toCreateOrUpdateTaskItemDto } from './mapping';
import { backendValidator } from '@/validation/validators/backendValidator';
import { AggregatedAjvError } from '@/validation/createSchemaValidator';

/** Translated label for a task type, e.g. "Manual Task". */
export function taskTypeLabel(taskType?: TaskTypeDescription | null): string {
  return taskType ? t(`TaskTypeDescriptionEnum.${taskType}`) : '';
}

export function useDeleteTaskWithConfirmation() {
  return useActionWithConfirmation({
    confirmedAction: async (id: string) =>
      await api.taskItemsApi.deleteTaskItem({
        id,
      }),
  });
}

export function useDeleteTaskAreaWithConfirmation(areas: FieldArrayContext<TaskItemAreaDto>) {
  return useActionWithConfirmation({
    confirmedAction: (index: number) => areas.remove(index),
  });
}

function validateXFieldEdges(
  beamShapingGroup: BeamShapingGroupRectangularDto,
  beamShapingGroupIndex: number,
  areaIndex: number,
  errors: { path: string; errors: string[] }[],
): void {
  if (
    typeof beamShapingGroup.radiationFieldEdgeX1?.value === 'number' &&
    typeof beamShapingGroup.radiationFieldEdgeX2?.value === 'number'
  ) {
    const isX2GreaterThanOrEqualToX1 =
      beamShapingGroup.radiationFieldEdgeX2.value >= beamShapingGroup.radiationFieldEdgeX1.value;
    if (!isX2GreaterThanOrEqualToX1) {
      errors.push({
        path: `taskItemAreas[${areaIndex}].medicalSystemSetup.beamShapingContainer.selectedBeamShapings[${beamShapingGroupIndex}].radiationFieldEdgeX2`,
        errors: [
          t('ValueTwoMustBeGreaterThanOrEqualToValueOne', {
            valueTwo: 'X2',
            valueOne: 'X1',
          }),
        ],
      });
    }
    const isX1LessThanOrEqualToX2 =
      beamShapingGroup.radiationFieldEdgeX1.value <= beamShapingGroup.radiationFieldEdgeX2.value;
    if (!isX1LessThanOrEqualToX2) {
      errors.push({
        path: `taskItemAreas[${areaIndex}].medicalSystemSetup.beamShapingContainer.selectedBeamShapings[${beamShapingGroupIndex}].radiationFieldEdgeX1`,
        errors: [
          t('ValueOneMustBeLessThanOrEqualTwoValueTwo', {
            valueOne: 'X1',
            valueTwo: 'X2',
          }),
        ],
      });
    }
  }
}

function validateYFieldEdges(
  beamShapingGroup: BeamShapingGroupRectangularDto,
  beamShapingGroupIndex: number,
  areaIndex: number,
  errors: { path: string; errors: string[] }[],
): void {
  if (
    typeof beamShapingGroup.radiationFieldEdgeY1?.value === 'number' &&
    typeof beamShapingGroup.radiationFieldEdgeY2?.value === 'number'
  ) {
    const isY2GreaterThanOrEqualToY1 =
      beamShapingGroup.radiationFieldEdgeY2.value >= beamShapingGroup.radiationFieldEdgeY1.value;
    if (!isY2GreaterThanOrEqualToY1) {
      errors.push({
        path: `taskItemAreas[${areaIndex}].medicalSystemSetup.beamShapingContainer.selectedBeamShapings[${beamShapingGroupIndex}].radiationFieldEdgeY2`,
        errors: [
          t('ValueTwoMustBeGreaterThanOrEqualToValueOne', {
            valueTwo: 'Y2',
            valueOne: 'Y1',
          }),
        ],
      });
    }
    const isY1LessThanOrEqualToY2 =
      beamShapingGroup.radiationFieldEdgeY1.value <= beamShapingGroup.radiationFieldEdgeY2.value;
    if (!isY1LessThanOrEqualToY2) {
      errors.push({
        path: `taskItemAreas[${areaIndex}].medicalSystemSetup.beamShapingContainer.selectedBeamShapings[${beamShapingGroupIndex}].radiationFieldEdgeY1`,
        errors: [
          t('ValueOneMustBeLessThanOrEqualTwoValueTwo', {
            valueOne: 'Y1',
            valueTwo: 'Y2',
          }),
        ],
      });
    }
  }
}

export const validateFieldEdges: CustomValidateFunction<TaskItemDto> = (data) => {
  const errors: AggregatedAjvError[] = [];
  if (instanceOfTaskItemDto(data)) {
    data.taskItemAreas?.forEach((area, areaIndex) => {
      if (area.medicalSystemSetup && instanceOfMvTherapySystemSetupDto(area.medicalSystemSetup)) {
        area.medicalSystemSetup.beamShapingContainer?.selectedBeamShapings?.forEach(
          (beamShapingGroup: BeamShapingGroupDto, beamShapingGroupIndex: number) => {
            if (instanceOfBeamShapingGroupRectangularDto(beamShapingGroup)) {
              validateXFieldEdges(beamShapingGroup, beamShapingGroupIndex, areaIndex, errors);
              validateYFieldEdges(beamShapingGroup, beamShapingGroupIndex, areaIndex, errors);
            }
          },
        );
      }
    });
  }
  return errors;
};

export const validateDoseRates: CustomValidateFunction<TaskItemDto> = (data) => {
  const errors: AggregatedAjvError[] = [];
  if (instanceOfTaskItemDto(data)) {
    data.taskItemAreas?.forEach((area, areaIndex) => {
      if (
        area.medicalSystemSetup &&
        instanceOfMvTherapySystemSetupDto(area.medicalSystemSetup) &&
        area.medicalSystemSetup.beamQualityContainer?.isRequired
      ) {
        const selectedPhotonDoseRatesIsEnabled = [
          ...area.medicalSystemSetup.beamQualityContainer.beamQualitiesPhoton,
          ...area.medicalSystemSetup.beamQualityContainer.beamQualitiesElectron,
        ].some((doseRate) => doseRate.isEnabled);

        if (!selectedPhotonDoseRatesIsEnabled) {
          errors.push({
            path: `taskItemAreas[${areaIndex}].medicalSystemSetup.beamQualityContainer`,
            errors: [t('AtLeastOneEnergyMustBeSelected')],
          });
        }
      }
    });
  }
  return errors;
};

export const validateEvaluationScopes: CustomValidateFunction<TaskItemDto> = (data) => {
  const errors: AggregatedAjvError[] = [];

  if (data.taskType === 'BeamConstancyCheck') {
    if (!data.taskItemAreas) return;
    data.taskItemAreas?.forEach((area, areaIndex) => {
      if (area.medicalSystemSetup?.targetSystemEntityId) {
        area.evaluationScopes?.forEach((scope) => {
          let paramEnabledFound = false;

          scope.evaluationParameters?.forEach((param) => {
            if (param.isEnabled) {
              paramEnabledFound = true;
            }
          });

          if (!paramEnabledFound) {
            errors.push({
              path: `taskItemAreas[${areaIndex}].evaluationScopes[0].evaluationParameters`,
              errors: [t('AtLeastOneEvaluationMustBeEnabled')],
            });
          }
        });
      }
    });
  }

  return errors;
};

export const validateParameters: CustomValidateFunction<TaskItemDto> = (data) => {
  const errors: AggregatedAjvError[] = [];

  const checkForErrorsInParams = (params: Array<any>, path: string) => {
    params.forEach((param, paramIndex) => {
      if (instanceOfNumericListDto(param) || instanceOfTextValueListDto(param)) {
        const savedValues: Array<number | string> = [];
        param.values?.forEach((value, valueIndex) => {
          // check for empty field
          if (value === undefined || value === null) {
            errors.push({
              path: `${path}[${paramIndex}].values[${valueIndex}]`,
              errors: [t('Validation.PropertyRequired')],
            });
          }
          // check for duplicates
          if (savedValues.find((item) => item === value)) {
            errors.push({
              path: `${path}[${paramIndex}].values[${valueIndex}]`,
              errors: [t('Validation.ThisFieldMustBeUnique')],
            });
          }

          savedValues.push(value);
        });
      }
    });
  };

  data.taskItemAreas?.forEach((area, areaIndex) => {
    if (area.medicalSystemSetup?.targetSystemEntityId) {
      checkForErrorsInParams(
        area.medicalSystemSetup.singleSetupParameters,
        `taskItemAreas[${areaIndex}].medicalSystemSetup.singleSetupParameters`,
      );
    }
    if (area.qaEquipmentSetups) {
      area.qaEquipmentSetups.forEach((setup, index2) => {
        if (setup.singleSetupParameters) {
          checkForErrorsInParams(
            setup.singleSetupParameters,
            `taskItemAreas[${areaIndex}].qaEquipmentSetups[${index2}].singleSetupParameters`,
          );
        }
      });
    }
  });
  return errors;
};

export const taskBackendValidation = backendValidator((taskItemDto: TaskItemDto, errorHandlers) =>
  api.taskItemsApi.validateTaskItem(
    {
      createOrUpdateTaskItemDto: toCreateOrUpdateTaskItemDto(taskItemDto),
    },
    errorHandlers,
  ),
);

/**
 * Generates a formatted string representing the name of a setup parameter, optionally including the geometry name.
 *
 * @param parameter - The `EntityWithOrigin` object representing the setup parameter.
 * @param options - An optional object with the following properties:
 *   - `ignoreGeometry`: If true, only the name of the parameter will be returned.
 * @returns A formatted string representing the name of the setup parameter, or the EMPTY_FIELD_SIGN if the parameter is not provided.
 */
export function getSetupParameterName(
  parameter?: TranslatableEntity,
  options?: { ignoreGeometry?: boolean },
) {
  if (!parameter) {
    return EMPTY_FIELD_SIGN;
  }

  const strings = [translateEntityProperty('name', parameter)];

  if (parameter.geometry && !options?.ignoreGeometry) {
    strings.push(translateEntityProperty('name', parameter.geometry));
  }

  return strings.filter(truthy).join(' ');
}

// This function is used to format statistic-values
export function toStatisticsDisplayValue(
  parameter: NumericEvaluationParameterDto,
  value?: number,
  additionalDecimalPlaces: number = 0,
): string {
  const formattingOptions = getFormattingOptionsFromEvaluationParameter(parameter);

  // (AT-14: https://ptw22/#page?projectName=NMD%20P3.4.0%20Framework&dbid=AT%2014%2059).
  return toDisplayValue(value, {
    // All values are treated as floats even if its an integer with a trailing zero. This is per requirement
    valueType: 'FloatNumber' as QuantityValueType,
    unitAbbreviation: formattingOptions?.unitAbbreviation,
    fractionDigits: (formattingOptions?.fractionDigits ?? 1) + additionalDecimalPlaces,
  });
}

export function checkLimits(min: number | undefined | null, max: number | undefined | null) {
  return !(min !== undefined && min !== null && max !== undefined && max !== null && min >= max);
}
